import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { Side } from '@/common/constants.ts';
import type { PriceLevel } from '@/feature/order_book/types.ts';
import { CURRENT_APP_VERSION, clearDataMigration } from '@/utils/zustand_helpers.ts';
import { environment } from '../../../environment.ts';
import { type FillPrognosis, LevelAggregator } from './LevelAggregator';
import type { NormalizedPriceLevel, OrderBookPriceUpdate } from './types.ts';

// aggregators are stored outside the store for performance reasons
let bidAggregator: LevelAggregator | null = null;
let askAggregator: LevelAggregator | null = null;

export type OrderBookConnectionStatus = 'disconnected' | 'connecting' | 'connected';

export interface OrderBookState {
  symbol: string;
  bidLevels: PriceLevel[];
  askLevels: PriceLevel[];
  spread?: string;
  spreadPercentage?: number;
  lastUpdateId: number;
  status: OrderBookConnectionStatus;
  error?: string;
  tickSize: string;
  tickSizeByMarket: Record<string, string>;
  quoteDecimals: number;
  assetDecimals: number;
  bestBidPrice?: number;
  bestAskPrice?: number;
}

export interface OrderBookActions {
  connect: (symbol: string) => void;
  disconnect: () => void;
  reset: () => void;
  setSnapshot: (snapshot: { bids: NormalizedPriceLevel[]; asks: NormalizedPriceLevel[]; lastUpdateId: number }) => void;
  updateOrderBook: (update: OrderBookPriceUpdate) => void;
  setError: (error?: string) => void;
  setTickSize: (tickSize: string, marketKey?: string) => void;
  setDecimals: (quoteDecimals: number, assetDecimals: number) => void;
  getFillPrognosis: (side: Side, assetSize: number, slippagePct?: number) => FillPrognosis | undefined;
}

export type OrderBookStore = OrderBookState & OrderBookActions;

const initialBookData = {
  bidLevels: [],
  askLevels: [],
  spread: undefined,
  spreadPercentage: undefined,
  error: undefined,
  bestBidPrice: undefined,
  bestAskPrice: undefined,
};

const initialState: OrderBookState = {
  symbol: '',
  ...initialBookData,
  lastUpdateId: 0,
  status: 'disconnected',
  tickSize: '0.1',
  tickSizeByMarket: {},
  quoteDecimals: 1,
  assetDecimals: 5,
};

export const DEFAULT_TICK_USD_BY_SYMBOL: Record<string, number> = {
  SOL: 0.01,
  HYPE: 0.001,
  MON: 0.000005,
};

// Persisted tick while still offered, else the symbol's preferred USD default (smallest offered
// tick >= it, or the largest offered when none reach it), else the smallest offered tick.
export const resolveMarketTickSize = (
  persisted: string | undefined,
  tickLevels: string[],
  defaultTickUsd?: number
): string | undefined => {
  if (persisted && tickLevels.includes(persisted)) return persisted;
  if (defaultTickUsd != null && tickLevels.length > 0) {
    return tickLevels.find((tick) => Number(tick) >= defaultTickUsd) ?? tickLevels[tickLevels.length - 1];
  }
  return tickLevels[0];
};

export const useOrderBookStore = create<OrderBookStore>()(
  persist(
    subscribeWithSelector(
      immer((set, get) => {
        const updateSpread = (draft: any) => {
          draft.bestBidPrice = bidAggregator?.getBestPrice();
          draft.bestAskPrice = askAggregator?.getBestPrice();

          if (draft.bestBidPrice !== undefined && draft.bestAskPrice !== undefined) {
            const spread = draft.bestAskPrice - draft.bestBidPrice;
            if (spread >= 0) {
              draft.spread = spread.toFixed(draft.quoteDecimals);
              draft.spreadPercentage = draft.bestBidPrice > 0 ? (spread / draft.bestBidPrice) * 100 : 0;
            } else {
              draft.spread = undefined;
              draft.spreadPercentage = undefined;
            }
          } else {
            draft.spread = undefined;
            draft.spreadPercentage = undefined;
          }
        };

        return {
          ...initialState,

          connect: (symbol: string) => {
            set((draft) => {
              Object.assign(draft, initialBookData);
              draft.symbol = symbol;
              draft.status = 'connecting';

              // Reset aggregators for new symbol
              bidAggregator = null;
              askAggregator = null;
            });
          },

          disconnect: () => {
            set((draft) => {
              draft.status = 'disconnected';
            });
          },

          reset: () => {
            set((draft) => {
              Object.assign(draft, initialBookData);
              draft.lastUpdateId = 0;
              draft.status = 'disconnected';
            });
            bidAggregator = null;
            askAggregator = null;
          },

          setSnapshot: (snapshot) => {
            set((draft) => {
              const { quoteDecimals, assetDecimals, tickSize } = get();
              bidAggregator = new LevelAggregator(tickSize, quoteDecimals, assetDecimals, true);
              askAggregator = new LevelAggregator(tickSize, quoteDecimals, assetDecimals, false);

              bidAggregator.buildFromSnapshot(snapshot.bids);
              askAggregator.buildFromSnapshot(snapshot.asks);

              draft.bidLevels = bidAggregator.getLevels();
              draft.askLevels = askAggregator.getLevels();

              updateSpread(draft);

              draft.lastUpdateId = snapshot.lastUpdateId;
              draft.status = 'connected';
            });
          },

          updateOrderBook: (update) => {
            const state = get();

            if (state.status !== 'connected') {
              console.log('Attempt to update disconnected orderbook');
              return;
            }

            // Stale event: everything in it is already reflected in our state
            // (snapshot overlap after subscribe, or duplicate WS delivery).
            if (update.u <= state.lastUpdateId) {
              return;
            }

            // update.U === update.u === L2Book.at.b (a block number, not a sequence number).
            // L2 updates only fire on blocks with order-book changes, so skipping blocks is expected.
            // Log skips in dev only so we can sanity-check the assumption without flooding prod consoles;
            // true message-loss is caught by the heartbeat `sn` check in useMarketDataWebSocket.
            if (!environment.production && update.U > state.lastUpdateId + 1) {
              console.debug(
                `Order book block skip: last=${state.lastUpdateId}, next=${update.U} (${update.U - state.lastUpdateId - 1} blocks skipped)`
              );
            }

            if (update.b.length === 0 && update.a.length === 0) {
              set((draft) => {
                draft.lastUpdateId = update.u;
              });
              return;
            }

            set((draft) => {
              if (!bidAggregator || !askAggregator) {
                if (!environment.production) {
                  throw new Error('Order book aggregators missing on update - store flow is broken');
                } else {
                  console.log('Order aggregators missing during update - check flow');
                  return;
                }
              }

              if (update.b.length > 0) {
                bidAggregator.updateLevels(update.b);
                draft.bidLevels = bidAggregator.getLevels();
              }

              if (update.a.length > 0) {
                askAggregator.updateLevels(update.a);
                draft.askLevels = askAggregator.getLevels();
              }
              updateSpread(draft);
              draft.lastUpdateId = update.u;
            });
          },

          setError: (error) => {
            set((draft) => {
              draft.error = error;
              draft.status = 'disconnected';
            });
          },

          setTickSize: (tickSize, marketKey) => {
            set((draft) => {
              if (marketKey) {
                draft.tickSizeByMarket[marketKey] = tickSize;
              }
              if (tickSize === draft.tickSize) {
                return;
              }
              draft.tickSize = tickSize;

              if (bidAggregator && askAggregator) {
                bidAggregator.setTickSize(tickSize);
                askAggregator.setTickSize(tickSize);

                draft.bidLevels = bidAggregator.getLevels();
                draft.askLevels = askAggregator.getLevels();
              }
            });
          },

          setDecimals: (quoteDecimals, assetDecimals) => {
            set((draft) => {
              draft.quoteDecimals = quoteDecimals;
              draft.assetDecimals = assetDecimals;
            });
          },

          // Market BUY lifts asks; market SELL hits bids. Mid is the baseline for the displayed
          // impact %, this side's best for the slippage limit (matches the backend). One-sided
          // book: mid is unavailable and the aggregator falls back to best for impact too.
          getFillPrognosis: (side, assetSize, slippagePct) => {
            const aggregator = side === Side.long ? askAggregator : bidAggregator;
            if (!aggregator) return undefined;
            const bestBid = bidAggregator?.getBestPrice();
            const bestAsk = askAggregator?.getBestPrice();
            const mid = bestBid != null && bestAsk != null ? (bestBid + bestAsk) / 2 : undefined;
            const sideBest = side === Side.long ? bestAsk : bestBid;
            const limit =
              sideBest != null && slippagePct != null && slippagePct > 0
                ? sideBest * (1 + ((side === Side.long ? 1 : -1) * slippagePct) / 100)
                : undefined;
            return aggregator.getFillPrognosis(assetSize, mid, limit);
          },
        };
      })
    ),
    {
      name: 'dex-order-book-v2',
      version: CURRENT_APP_VERSION,
      migrate: clearDataMigration,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        symbol: state.symbol,
        tickSize: state.tickSize,
        tickSizeByMarket: state.tickSizeByMarket,
        quoteDecimals: state.quoteDecimals,
        assetDecimals: state.assetDecimals,
      }),
    }
  )
);
